import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./counter-number-editor.module.scss";
import { useI18N } from "@src/language/language-context";
import { Widget, WidgetSemantics } from "@src/types/dashboards";
import { MachineNameAndSourceId } from "@common/types/server-list-types";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitOptionLabelOf } from "@src/cloud-connect/units/utils/unit-option-label-of";
import type { UnitDefinition } from "@src/types/units";
import {
  selectUnitsById,
  selectUnitsStatus,
  unitsFailed,
  unitsLoaded,
  unitsRequested,
} from "@src/reducers/units-slice";
import { getAllSourcesListUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-all-sources-list-use-case";
import { getActiveTimeseriesUseCase } from "@src/cloud-connect/timeseries/use-cases/get-active-timeseries-use-case";
import { getUnitsUseCase } from "@src/cloud-connect/timeseries/use-cases/get-units-use-case";
import {
  GaugeBoundNode,
  buildWidgetQuery,
  getWidgetDataSource,
} from "@src/components/organisms/widgets/widget-query";
import {
  DEFAULT_POLLING_INTERVAL,
  DURATION_GAUGE_WIDGET_MS,
  durationToIso,
  isoToDurationMs,
} from "@src/components/organisms/widgets/constants";
import {
  getNumericNodeUnit,
  toNumericNodeOptions,
} from "@src/components/organisms/widgets/node-options";
import {
  buildScaleTransforms,
  scaleForField,
  scaleTransformsKey,
} from "@src/components/organisms/widgets/utils/value-scaling";
import { parseUserNumber } from "@src/components/organisms/widgets/utils/parse-user-number";
import { MachineNodeUnitFields } from "@src/components/molecules/machine-node-unit-fields/machine-node-unit-fields";
import { LookbackAndPollingFields } from "@src/components/molecules/lookback-and-polling-fields/lookback-and-polling-fields";
import { ScaleInput } from "@src/components/atoms/scale-input/scale-input";
import { ThresholdFields } from "@src/components/molecules/threshold-fields/threshold-fields";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { InputWithToggle } from "@src/components/atoms/input-with-toggle/input-with-toggle";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import Spinner from "@src/components/atoms/spinner/spinner";
import { useSaveWidget } from "../hooks/use-save-widget";

const cssClassName = "counter-number-editor";

export interface CounterNumberEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

/**
 * Editor for a `counter-number` (gauge) widget: the machine / node / unit it
 * reads, the optional target, the min/max dial range (each of which can come
 * from a typed number or from an OPC-UA node), the warning / critical
 * thresholds and the lookback window. Owns its own state and its own save.
 */
export const CounterNumberEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: CounterNumberEditorProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const units = useSelector(selectUnitsById);
  const unitsStatus = useSelector(selectUnitsStatus);
  const isLoadingUnits = unitsStatus === "idle" || unitsStatus === "loading";

  const savedSemantics = widget.semantics as Partial<WidgetSemantics> & {
    target_manual?: number | null;
    min_value?: number | null;
    max_value?: number | null;
    thresholds?: { warning?: number | null; critical?: number | null } | null;
  };
  // A bound is node-driven exactly when its binding is present — that is what
  // the save path writes, and the two modes are mutually exclusive.
  const savedBoundNode = (
    field: GaugeBoundNode["field"],
  ): MachineNameAndSourceId | null => {
    const address = widget.query?.bindings?.find(
      (binding) => binding.field === field,
    )?.address;
    return address ? { name: address, source_id: address } : null;
  };
  // Pending widgets ignore the template's unit; an existing one keeps it.
  const savedUnitId = isPendingWidget
    ? null
    : widget.presentation?.unit ?? null;
  const savedDataSource = useMemo(
    () => getWidgetDataSource(widget.query),
    [widget.query],
  );

  const [title, setTitle] = useState(widget.title);
  const [target, setTarget] = useState(
    savedSemantics.target_manual != null
      ? String(savedSemantics.target_manual)
      : "",
  );
  const [min, setMin] = useState(
    savedSemantics.min_value != null ? String(savedSemantics.min_value) : "",
  );
  const [max, setMax] = useState(
    savedSemantics.max_value != null ? String(savedSemantics.max_value) : "",
  );
  const [warningEnabled, setWarningEnabled] = useState(
    savedSemantics.thresholds?.warning != null,
  );
  const [warningValue, setWarningValue] = useState(
    savedSemantics.thresholds?.warning != null
      ? String(savedSemantics.thresholds.warning)
      : "",
  );
  const [criticalEnabled, setCriticalEnabled] = useState(
    savedSemantics.thresholds?.critical != null,
  );
  const [criticalValue, setCriticalValue] = useState(
    savedSemantics.thresholds?.critical != null
      ? String(savedSemantics.thresholds.critical)
      : "",
  );
  const [machine, setMachine] = useState<MachineNameAndSourceId | null>(
    savedDataSource
      ? { name: savedDataSource.group, source_id: savedDataSource.group }
      : null,
  );
  const [node, setNode] = useState<MachineNameAndSourceId | null>(
    savedDataSource
      ? { name: savedDataSource.source, source_id: savedDataSource.source }
      : null,
  );
  // The saved node is restored once, when the editor opens on its stored
  // machine. Once the user has picked a machine by hand, recalling the same
  // one must not bring the old node back.
  const machineTouchedRef = useRef(false);
  // The three gauge bounds. The picked node is kept while the toggle is off,
  // so flipping back and forth does not lose it.
  const [targetNode, setTargetNode] = useState<MachineNameAndSourceId | null>(
    () => savedBoundNode("target"),
  );
  const [targetFromNode, setTargetFromNode] = useState(
    () => savedBoundNode("target") != null,
  );
  const [minNode, setMinNode] = useState<MachineNameAndSourceId | null>(() =>
    savedBoundNode("min"),
  );
  const [minFromNode, setMinFromNode] = useState(
    () => savedBoundNode("min") != null,
  );
  const [maxNode, setMaxNode] = useState<MachineNameAndSourceId | null>(() =>
    savedBoundNode("max"),
  );
  const [maxFromNode, setMaxFromNode] = useState(
    () => savedBoundNode("max") != null,
  );
  const [unit, setUnit] = useState<UnitDefinition | null>(() =>
    getUnitById(units, savedUnitId),
  );
  // Auto-pick re-evaluates on every node change; a manual pick
  // (handleUnitSelection) turns it off until the node changes again.
  const [shouldAutoPickUnit, setShouldAutoPickUnit] = useState(
    savedUnitId == null,
  );
  const [lookbackMs, setLookbackMs] = useState<number | null>(
    isoToDurationMs(widget.query?.window?.duration) ?? DURATION_GAUGE_WIDGET_MS,
  );
  const [pollingInterval, setPollingInterval] = useState<string | null>(
    widget.query?.sampling?.polling_interval ?? DEFAULT_POLLING_INTERVAL,
  );
  const [valueScale, setValueScale] = useState(
    String(scaleForField("value", widget.encoding?.transforms)),
  );
  const [machineOptions, setMachineOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [numericNodeOptions, setNumericNodeOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [nodeUnits, setNodeUnits] = useState<Record<string, number | null>>({});
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const texts = {
    saveButton: i18n("modules.counterNumber.saveButton"),
    cancelButton: i18n("modules.counterNumber.cancelButton"),
    titleLabel: i18n("modules.counterNumber.titleLabel"),
    titleInputPlaceholder: i18n("modules.counterNumber.titleInputPlaceholder"),
    machineLabel: i18n("modules.counterNumber.machineLabel"),
    noMachines: i18n("modules.counterNumber.noMachines"),
    machinesPlaceholder: i18n("modules.counterNumber.machinesPlaceholder"),
    nodeLabel: i18n("modules.counterNumber.nodeLabel"),
    useOpcuaNode: i18n("modules.counterNumber.useOpcuaNode"),
    noNodes: i18n("modules.counterNumber.noNodes"),
    nodesPlaceholder: i18n("modules.counterNumber.nodesPlaceholder"),
    unitLabel: i18n("modules.counterNumber.unitLabel"),
    noUnits: i18n("modules.counterNumber.noUnits"),
    unitsPlaceholder: i18n("modules.counterNumber.unitsPlaceholder"),
    targetLabel: i18n("modules.counterNumber.targetLabel"),
    targetInputPlaceholder: i18n(
      "modules.counterNumber.targetInputPlaceholder",
    ),
    targetInvalid: i18n("modules.counterNumber.targetInvalid"),
    thresholdLabel: i18n("modules.counterNumber.thresholdLabel"),
    warningLabel: i18n("modules.counterNumber.warningLabel"),
    criticalLabel: i18n("modules.counterNumber.criticalLabel"),
    minLabel: i18n("modules.counterNumber.minLabel"),
    minInputPlaceholder: i18n("modules.counterNumber.minInputPlaceholder"),
    maxLabel: i18n("modules.counterNumber.maxLabel"),
    maxInputPlaceholder: i18n("modules.counterNumber.maxInputPlaceholder"),
    titleRequired: i18n("modules.counterNumber.titleRequired"),
    machineRequired: i18n("modules.counterNumber.machineRequired"),
    nodeRequired: i18n("modules.counterNumber.nodeRequired"),
    lookbackRequired: i18n("modules.counterNumber.lookbackRequired"),
    minRequired: i18n("modules.counterNumber.minRequired"),
    maxRequired: i18n("modules.counterNumber.maxRequired"),
    warningValueRequired: i18n("modules.counterNumber.warningValueRequired"),
    criticalValueRequired: i18n("modules.counterNumber.criticalValueRequired"),
    thresholdOrderInvalid: i18n("modules.counterNumber.thresholdOrderInvalid"),
    scaleInvalid: i18n("modules.counterNumber.scaleInvalid"),
  };

  const unitOptions = Object.entries(units).map(([key, value]) => {
    const text =
      value.unit_text?.eng ?? Object.values(value.unit_text ?? {})[0];
    const label = unitOptionLabelOf(text, value.unit_id);
    return {
      name: label,
      searchText: [
        value.unit_id,
        value.external_reference,
        text?.name,
        text?.symbol,
        text?.description,
      ]
        .filter(Boolean)
        .join(" "),
      source_id: key,
    };
  });
  const selectedUnitEntry = unit
    ? Object.entries(units).find(
        ([key, candidate]) =>
          key === unit.unit_id || candidate.unit_id === unit.unit_id,
      )
    : undefined;
  const selectedUnitOption = unit
    ? {
        name: unitOptionLabelOf(unit.unit_text?.eng, unit.unit_id),
        source_id: selectedUnitEntry?.[0] ?? unit.unit_id,
      }
    : null;
  const findUnitByReference = (reference: number | string | null) =>
    reference == null
      ? undefined
      : Object.values(units).find(
          (u) => u.external_reference === String(reference),
        );

  // Picking a unit and clearing the field both stop the auto-pick; only
  // picking a node starts it again. Auto-pick writes below do NOT change the
  // flag — writing isn't picking.
  const handleUnitSelection = (selected: MachineNameAndSourceId | null) => {
    setUnit(
      selected
        ? units[selected.source_id] ??
            Object.values(units).find(
              (candidate) => candidate.unit_id === selected.source_id,
            ) ??
            null
        : null,
    );
    setShouldAutoPickUnit(false);
  };

  // Once both machines and the initial node load are complete, reveal the form.
  useEffect(() => {
    if (!isInitializing) return;
    if (
      !isLoadingMachines &&
      !isLoadingUnits &&
      !(machine !== null && isLoadingNodes)
    ) {
      setIsInitializing(false);
    }
  }, [isLoadingMachines, isLoadingNodes, unitsStatus, machine, isInitializing]);

  // Load machines once.
  useEffect(() => {
    setIsLoadingMachines(true);
    getAllSourcesListUseCase().then((result) => {
      if (result.success && result.data) {
        setMachineOptions(
          Object.values(result.data).map((s) => ({
            name: s.name,
            source_id: s.source_id,
          })),
        );
      } else {
        setMachineOptions([]);
        console.error("Failed to load machines:", result.error);
      }
      setIsLoadingMachines(false);
    });
  }, []);

  // Units come from the global slice (loaded at dashboards-load). Lazy
  // fallback: if the slice was never attempted, fetch once so the dropdown
  // isn't empty.
  const unitsAttempted = useRef(false);
  useEffect(() => {
    if (unitsStatus !== "idle" || unitsAttempted.current) return;
    unitsAttempted.current = true;
    dispatch(unitsRequested());
    getUnitsUseCase().then((result) => {
      if (result.success && result.data) {
        dispatch(unitsLoaded(result.data));
      } else {
        dispatch(unitsFailed(result.error ?? "Failed to fetch units"));
      }
    });
  }, [unitsStatus, dispatch]);

  // The sources list loads asynchronously, so the prefill hands the machine id
  // forward and this effect swaps in the real name once the options arrive.
  useEffect(() => {
    if (machineOptions.length === 0) return;
    const machineId = savedDataSource?.group ?? widget.query?.source;
    if (!machineId) return;
    const match = machineOptions.find((m) => m.source_id === machineId);
    if (!match) return;
    setMachine((prev) => {
      if (!prev) return prev;
      // If the user is currently looking at a DIFFERENT machine, do NOT
      // overwrite — the source_id test stops that race.
      if (prev.source_id !== match.source_id) return prev;
      if (prev.name === match.name) return prev;
      return { name: match.name, source_id: match.source_id };
    });
  }, [machineOptions, savedDataSource, widget.query?.source]);

  // Same raw-id → human-name swap for the node and for the three bounds, which
  // resolve independently of each other (the value node may not be in the
  // options at all while a bound still is).
  useEffect(() => {
    if (numericNodeOptions.length === 0) return;
    if (savedDataSource) {
      const match = numericNodeOptions.find(
        (n) => n.source_id === savedDataSource.source,
      );
      if (match) {
        setNode((prev) => {
          if (!prev) return prev;
          if (prev.source_id !== match.source_id) return prev;
          if (prev.name === match.name) return prev;
          return { name: match.name, source_id: match.source_id };
        });
      }
    }
    const resolveBound = (
      setBound: React.Dispatch<
        React.SetStateAction<MachineNameAndSourceId | null>
      >,
    ) =>
      setBound((prev) => {
        if (!prev) return prev;
        const boundMatch = numericNodeOptions.find(
          (n) => n.source_id === prev.source_id,
        );
        if (!boundMatch || prev.name === boundMatch.name) return prev;
        return { name: boundMatch.name, source_id: boundMatch.source_id };
      });
    resolveBound(setTargetNode);
    resolveBound(setMinNode);
    resolveBound(setMaxNode);
  }, [numericNodeOptions, savedDataSource]);

  // Load nodes when the machine is selected.
  useEffect(() => {
    if (!machine) {
      setNumericNodeOptions([]);
      setNodeUnits({});
      setNode(null);
      return;
    }
    setIsLoadingNodes(true);
    let cancelled = false;
    getActiveTimeseriesUseCase(machine.source_id).then((result) => {
      if (cancelled) return;
      if (result.success && result.data) {
        // Only numeric nodes are consumable by the gauge.
        setNumericNodeOptions(toNumericNodeOptions(result.data.timeseries));
        setNodeUnits(
          Object.fromEntries(
            Object.values(result.data.timeseries)
              .filter((timeseries) => timeseries.data_type === "numeric")
              .map((timeseries) => [
                timeseries.source,
                getNumericNodeUnit(result.data.timeseries, timeseries.source),
              ]),
          ),
        );
        // The stored node belongs to the stored machine: restore it when that
        // machine's options load, leave the node empty for any other machine.
        // Only while the user has not picked a machine by hand — recalling the
        // same machine must not resurrect the saved node.
        const ds = getWidgetDataSource(widget.query);
        if (
          !machineTouchedRef.current &&
          ds &&
          ds.group === machine.source_id &&
          ds.source
        ) {
          setNode({ name: ds.source, source_id: ds.source });
        }
      } else {
        setNumericNodeOptions([]);
        setNodeUnits({});
        console.error("Failed to load nodes:", result.error);
      }
      setIsLoadingNodes(false);
    });

    return () => {
      cancelled = true;
    };
  }, [machine, widget.query]);

  // Restore the saved unit once the catalog is available (the global load may
  // still be in flight when the editor opens).
  useEffect(() => {
    if (isPendingWidget) return;
    const id = widget.presentation?.unit;
    if (id == null) return;
    const match = getUnitById(units, id);
    if (match) setUnit(match);
  }, [units, widget.presentation?.unit, isPendingWidget]);

  // The picked node drives the unit: a node with a unit fills the field, a
  // node without one clears it so the user can type one. Picking or clearing
  // a unit (handleUnitSelection) suspends this until the node changes again.
  useEffect(() => {
    if (!shouldAutoPickUnit) return;
    const nodeUnit = node ? nodeUnits[node.source_id] : null;
    const match = nodeUnit != null ? findUnitByReference(nodeUnit) : undefined;
    setUnit(match ?? null);
  }, [node, nodeUnits, units, shouldAutoPickUnit]);

  const validate = (): string | null => {
    if (title.trim() === "") return texts.titleRequired;
    if (!machine) return texts.machineRequired;
    if (!node) return texts.nodeRequired;
    if (lookbackMs == null) return texts.lookbackRequired;
    // Each bound is answered either by a picked node (toggle on) or by the
    // typed number. A toggle that is on with nothing picked answers nothing.
    if (
      minFromNode ? !minNode : min.trim() === "" || parseUserNumber(min) == null
    ) {
      return texts.minRequired;
    }
    if (
      maxFromNode ? !maxNode : max.trim() === "" || parseUserNumber(max) == null
    ) {
      return texts.maxRequired;
    }
    // Target is OPTIONAL (empty → null), but non-numeric input is a
    // save-blocker: anything that doesn't parse is a typo, not an intent to
    // clear the field.
    if (
      !targetFromNode &&
      target.trim() !== "" &&
      parseUserNumber(target) == null
    ) {
      return texts.targetInvalid;
    }
    if (warningEnabled && parseUserNumber(warningValue) == null) {
      return texts.warningValueRequired;
    }
    if (criticalEnabled && parseUserNumber(criticalValue) == null) {
      return texts.criticalValueRequired;
    }
    if (
      warningEnabled &&
      criticalEnabled &&
      parseUserNumber(warningValue) != null &&
      parseUserNumber(criticalValue) != null &&
      (parseUserNumber(warningValue) as number) >
        (parseUserNumber(criticalValue) as number)
    ) {
      return texts.thresholdOrderInvalid;
    }
    if (valueScale.trim() !== "" && parseUserNumber(valueScale) === null) {
      return texts.scaleInvalid;
    }
    return null;
  };

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    // Bounds driven by a node, in the order they are bound. Only a bound whose
    // toggle is ON and whose node is picked is written — the validation above
    // already refuses an on-but-unpicked toggle.
    const gaugeBoundNodes: GaugeBoundNode[] = (
      [
        ["min", minFromNode ? minNode : null],
        ["max", maxFromNode ? maxNode : null],
        ["target", targetFromNode ? targetNode : null],
      ] as [GaugeBoundNode["field"], MachineNameAndSourceId | null][]
    ).flatMap(([field, boundNode]) =>
      boundNode == null ? [] : [{ field, address: boundNode.source_id }],
    );
    const thresholds: { warning?: number; critical?: number } = {};
    if (warningEnabled && parseUserNumber(warningValue) != null) {
      thresholds.warning = parseUserNumber(warningValue) as number;
    }
    if (criticalEnabled && parseUserNumber(criticalValue) != null) {
      thresholds.critical = parseUserNumber(criticalValue) as number;
    }
    const thresholdsOut =
      Object.keys(thresholds).length > 0 ? thresholds : null;

    const updated: Widget = {
      ...widget,
      title: title.trim() || widget.title,
      image_url: widget.image_url ?? null,
      query:
        machine && node
          ? {
              ...buildWidgetQuery(
                machine.source_id,
                node.source_id,
                pollingInterval ?? undefined,
                gaugeBoundNodes,
              ),
              // Lookback persists as ISO. Clearing the dropdown still sends a
              // duration — the constant default — so legacy payloads never see
              // an empty `window`.
              window: {
                duration: durationToIso(lookbackMs ?? DURATION_GAUGE_WIDGET_MS),
              },
            }
          : widget.query,
      semantics: {
        // Empty input → the user cleared the field: reset to null explicitly
        // instead of falling back to the saved value. A bound a node drives
        // drops its static number entirely, so the body never carries both
        // halves of the same bound (`undefined` is JSON-dropped).
        target_manual: targetFromNode
          ? undefined
          : target.trim() === ""
          ? null
          : parseUserNumber(target) ?? savedSemantics.target_manual,
        min_value: minFromNode
          ? undefined
          : min.trim() === ""
          ? null
          : parseUserNumber(min) ?? savedSemantics.min_value,
        max_value: maxFromNode
          ? undefined
          : max.trim() === ""
          ? null
          : parseUserNumber(max) ?? savedSemantics.max_value,
        thresholds: thresholdsOut,
      },
      encoding: {
        ...widget.encoding,
        ...scaleTransformsKey(
          widget.encoding?.transforms,
          ["value"],
          buildScaleTransforms([{ field: "value", scale: valueScale }]),
        ),
      },
      presentation: { ...widget.presentation, unit: unit?.unit_id ?? null },
    };

    save(updated, { validate, imageFile: null });
  };

  if (isInitializing) {
    return (
      <div className={styles[`${cssClassName}__loading`]}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles[`${cssClassName}__outer-wrapper`]}>
      <Input2025
        inputPlaceHolder={texts.titleInputPlaceholder}
        labelText={texts.titleLabel}
        inputValue={title}
        onChangeFunc={(e) => setTitle(e.target.value)}
        inputVariant="dark-background"
      />

      <MachineNodeUnitFields
        texts={{
          machineLabel: texts.machineLabel,
          noMachines: texts.noMachines,
          machinesPlaceholder: texts.machinesPlaceholder,
          nodeLabel: texts.nodeLabel,
          noNodes: texts.noNodes,
          nodesPlaceholder: texts.nodesPlaceholder,
          unitLabel: texts.unitLabel,
          noUnits: texts.noUnits,
          unitsPlaceholder: texts.unitsPlaceholder,
        }}
        machineOptions={machineOptions}
        selectedMachine={machine}
        onSelectMachine={(selected) => {
          machineTouchedRef.current = true;
          setMachine(selected);
          setNode(null);
          // The unit belongs to the node, so it goes when the node goes.
          setUnit(null);
          // The gauge's bound nodes all sit on this machine, so a machine
          // change invalidates the three picks — leaving them would write the
          // old machine's node ids against the new machine.
          setTargetNode(null);
          setMinNode(null);
          setMaxNode(null);
        }}
        isLoadingMachines={isLoadingMachines}
        nodeOptions={numericNodeOptions}
        selectedNode={node}
        onSelectNode={(selected) => {
          setNode(selected);
          setShouldAutoPickUnit(true);
        }}
        isLoadingNodes={isLoadingNodes}
        unitOptions={unitOptions}
        selectedUnit={selectedUnitOption}
        onSelectUnit={handleUnitSelection}
        isLoadingUnits={isLoadingUnits}
      />

      <div className={styles[`${cssClassName}__field`]}>
        <ScaleInput value={valueScale} onChange={setValueScale} />
      </div>

      {/* Optional; an empty field clears target_manual to null at save. The
          toggle swaps the number for a node whose live value drives the marker
          instead — the two are mutually exclusive, so only one reaches the
          body. */}
      <div className={styles[`${cssClassName}__field`]}>
        <InputWithToggle
          labelText={texts.targetLabel}
          toggleText={texts.useOpcuaNode}
          isNodeMode={targetFromNode}
          handleToggleClick={() => setTargetFromNode((prev) => !prev)}
          nodeOptions={numericNodeOptions}
          selectedNode={targetNode}
          onSelectNode={setTargetNode}
          noNodesText={texts.noNodes}
          nodesPlaceholderText={texts.nodesPlaceholder}
          isLoadingNodes={isLoadingNodes}
          inputPlaceHolder={texts.targetInputPlaceholder}
          inputValue={target}
          onInputChange={(e) => setTarget(e.target.value)}
        />
      </div>

      <LookbackAndPollingFields
        i18n={i18n}
        lookbackMs={lookbackMs}
        setLookbackMs={setLookbackMs}
        pollingInterval={pollingInterval}
        setPollingInterval={setPollingInterval}
      />

      {/* Each threshold is checkbox-gated: the value input shows only when
          enabled and is required then. Both off → thresholds null. */}
      <ThresholdFields
        texts={{
          thresholdLabel: texts.thresholdLabel,
          warningLabel: texts.warningLabel,
          criticalLabel: texts.criticalLabel,
        }}
        warningEnabled={warningEnabled}
        warningValue={warningValue}
        onWarningEnabledChange={setWarningEnabled}
        onWarningValueChange={setWarningValue}
        criticalEnabled={criticalEnabled}
        criticalValue={criticalValue}
        onCriticalEnabledChange={setCriticalEnabled}
        onCriticalValueChange={setCriticalValue}
      />

      <div className={styles[`${cssClassName}__row`]}>
        <InputWithToggle
          labelText={texts.minLabel}
          toggleText={texts.useOpcuaNode}
          isNodeMode={minFromNode}
          handleToggleClick={() => setMinFromNode((prev) => !prev)}
          nodeOptions={numericNodeOptions}
          selectedNode={minNode}
          onSelectNode={setMinNode}
          noNodesText={texts.noNodes}
          nodesPlaceholderText={texts.nodesPlaceholder}
          isLoadingNodes={isLoadingNodes}
          inputPlaceHolder={texts.minInputPlaceholder}
          inputValue={min}
          onInputChange={(e) => setMin(e.target.value)}
        />
      </div>
      <div className={styles[`${cssClassName}__row`]}>
        <InputWithToggle
          labelText={texts.maxLabel}
          toggleText={texts.useOpcuaNode}
          isNodeMode={maxFromNode}
          handleToggleClick={() => setMaxFromNode((prev) => !prev)}
          nodeOptions={numericNodeOptions}
          selectedNode={maxNode}
          onSelectNode={setMaxNode}
          noNodesText={texts.noNodes}
          nodesPlaceholderText={texts.nodesPlaceholder}
          isLoadingNodes={isLoadingNodes}
          inputPlaceHolder={texts.maxInputPlaceholder}
          inputValue={max}
          onInputChange={(e) => setMax(e.target.value)}
        />
      </div>

      <Spacer16 />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={texts.saveButton}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={texts.cancelButton}
          variant="secondary"
          // Cancel must NOT fire mid-save: it would dispatch
          // removePendingWidget while POST/PUT are still in flight and leak a
          // server-side orphan.
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
